<script lang="ts">
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import type { VerifyInputParamType } from '$shared/components/types';
  import InputValue from './InputValue.svelte';
  import AdditionalInput from '../AdditionalInput/AdditionalInput.svelte';
  import type { Snippet } from 'svelte';

  interface Props {
    hasMissingParams?: boolean;
    hasMissingSupportedParams?: boolean;
    paramList?: VerifyInputParamType[];
    additionalList?: VerifyInputParamType[];
    additionalItemActive?: string;
    disabled?: boolean;
    custom?: Snippet | undefined;
    onadd?: (param: any) => void;
    onchangeadditionalinput?: (detail: any) => void;
  }

  let {
    hasMissingParams = false,
    hasMissingSupportedParams = false,
    paramList = [],
    additionalList = [],
    additionalItemActive = '',
    disabled = false,
    custom = undefined,
    onadd,
    onchangeadditionalinput
  }: Props = $props();

  let expand = $state(false);
  let optionalParamLabels: string = $derived(paramList
      .filter((item) => item.optionalParams.length > 0)
      .map((item) => item.optionalParams?.first()?.label)
      .join(' or '));

  let iconValue = $derived(!expand ? 'icon-chevron-down active' : 'icon-chevron-up active');
  

  const onClickToggle = (): void => {
    expand = !expand;
  };

  const onChangeAdditionalInput = (detail: any): void => {
    onchangeadditionalinput?.(detail);
  };
</script>

<div data-cy="verificationInput" class="comp-verification-input">
  <!-- Header -->
  <div class="header">
    <button
      data-cy="verificationInput-btn"
      type="button"
      class="toggle-btn"
      onclick={onClickToggle}
    >
      <div class="text">Verification input</div>
      <div class="icon">
        <Icon value={iconValue} />
      </div>
    </button>
    <div data-cy="verificationInput-desc" class="desc">
      Provider information we'll use to run this verification
    </div>
    {#if hasMissingParams || hasMissingSupportedParams}
      <div data-cy="verificationInput-info" class="warn">Add information to improve results</div>
    {/if}
  </div>
  <!-- /Header -->

  <div data-cy="verificationInput-content" class="content" class:show={expand}>
    {#each paramList as list}
      <div class="list">
        {#each list.params as param}
          <div class="param-li" class:required={param.required}>
            <div data-cy={`${param.label}-k`} class="key">{param.label}</div>
            <div class="values">
              {#each param.values as value}
                <InputValue {disabled} name={param.label} {value} type={param.type} {onadd} />
              {/each}
            </div>
          </div>
        {/each}
        {#each list.optionalParams as param}
          <div class="param-li" class:hasOptionalrequired={param.required}>
            <div data-cy={`${param.label}-k`} class="key">{param.label}</div>
            <div class="values">
              {#each param.values as value}
                <InputValue {disabled} name={param.label} {value} type={param.type} {onadd} />
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/each}
    {@render custom?.()}
    {#if Array.isArray(additionalList) && additionalList.length !== 0}
      <AdditionalInput
        list={additionalList}
        active={additionalItemActive}
        {disabled}
        {onadd}
        onchange={onChangeAdditionalInput}
      />
    {/if}
    <div data-cy="verificationInput-content-desc" class="desc">* Required to run verification</div>
    {#if optionalParamLabels}
      <div data-cy="verificationInput-content-desc" class="desc">
        ** {optionalParamLabels} are required, Adding both will improve the results
      </div>
    {/if}
  </div>
</div>

<style>
  .comp-verification-input {
    background-color: var(--grey-bg);
    padding: 16px;
    border-radius: 5px;
  }
  .toggle-btn {
    color: var(--blue);
    font-weight: normal;
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 4px;
  }
  .toggle-btn,
  .toggle-btn .icon {
    display: flex;
    align-items: center;
  }
  .toggle-btn .icon {
    margin-top: 1px;
    margin-left: 4px;
  }

  .desc {
    font-weight: normal;
    font-size: 11px;
    line-height: 18px;
    color: var(--grey-medium);
  }

  .warn {
    font-weight: normal;
    font-size: 11px;
    line-height: 18px;
    font-style: italic;
    font-weight: 700;
  }

  .content {
    display: none;
  }

  .content.show {
    display: block;
    margin-top: 24px;
  }

  .param-li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .param-li .key {
    width: 100px;
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    position: relative;
    flex-shrink: 0;
    color: #1d1d21;
  }
  .param-li.required .key::after {
    content: '*';
    margin-left: 2px;
  }
  .param-li.hasOptionalrequired .key::after {
    content: '**';
    margin-left: 2px;
  }
</style>
